import { useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Recaptcha } from "@/components/Recaptcha";
import { PasswordFields } from "@/components/PasswordFields";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import { Briefcase, User, ArrowLeft, Check } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextParam = params.get("next");
  const next = nextParam && nextParam.startsWith("/") ? nextParam : null;
  const initialRole = params.get("role") === "subscriber" ? "subscriber" : "customer";
  const DRAFT_KEY = "bookdhub_signup_draft";
  // Restore a half-finished signup synchronously (before child effects run).
  // Password intentionally excluded for security.
  const [form, setForm] = useState(() => {
    const base = { name: "", email: "", password: "", role: initialRole, phone: "", sms_consent: false, customer_type: "individual", org_name: "", org_title: "", org_email: "", org_tax_id: "", authorized: false };
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) return { ...base, ...JSON.parse(raw), password: "" };
    } catch (e) { /* ignore malformed draft */ }
    return base;
  });
  const [step, setStep] = useState(1);
  const [pwValid, setPwValid] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const recaptchaRef = useRef(null);

  // Save on every real field change (never persists the password)
  const set = (k, v) => setForm((f) => {
    const nf = { ...f, [k]: v };
    try {
      const { password, ...rest } = nf;
      localStorage.setItem(DRAFT_KEY, JSON.stringify(rest));
    } catch (e) { /* ignore quota errors */ }
    return nf;
  });

  const goNext = () => {
    setError("");
    if (!form.name.trim()) { setError("Please enter your full name."); return; }
    if (!form.email.trim()) { setError("Please enter your email address."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) { setError("Please enter a valid email address."); return; }
    if (!form.phone.trim()) { setError("Mobile phone number is required."); return; }
    if (!pwValid) { setError("Please meet all password requirements and confirm they match."); return; }
    setStep(2);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (step === 1) { goNext(); return; }
    setError("");
    if (form.role === "customer" && form.customer_type === "business") {
      if (!form.org_name.trim() || !form.org_title.trim() || !form.org_email.trim() || !form.org_tax_id.trim()) {
        setError("Please complete all business details (organization, title, email, tax ID).");
        return;
      }
      if (!form.authorized) {
        setError("Please confirm you are authorized to book and pay on behalf of your organization.");
        return;
      }
    }
    setLoading(true);
    const captchaToken = await recaptchaRef.current?.execute().catch(() => null);
    if (!captchaToken) {
      setError("CAPTCHA verification failed. Please try again.");
      setLoading(false);
      return;
    }
    try {
      const isBiz = form.role === "customer" && form.customer_type === "business";
      const payload = {
        name: form.name, email: form.email, password: form.password, role: form.role,
        phone: form.phone, sms_consent: form.sms_consent, turnstile_token: captchaToken,
        ...(form.role === "customer" ? { customer_type: form.customer_type } : {}),
        ...(isBiz ? {
          org_name: form.org_name, org_title: form.org_title,
          org_email: form.org_email, org_tax_id: form.org_tax_id, authorized: form.authorized,
        } : {}),
      };
      const u = await register(payload);
      try { localStorage.removeItem(DRAFT_KEY); } catch (e) {}
      if (u && u.email_verified === false) {
        navigate(`/verify-email?email=${encodeURIComponent(form.email)}${next ? `&next=${encodeURIComponent(next)}` : ""}`);
        return;
      }
      if (next) navigate(next);
      else if (u.role === "subscriber") navigate("/dashboard");
      else if (u.customer_type === "business") navigate("/business");
      else navigate("/browse");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const roles = [
    { key: "customer", label: "I'm a Customer", desc: "Book services", icon: User },
    { key: "subscriber", label: "I'm a Provider", desc: "Offer services", icon: Briefcase },
  ];

  const StepDot = ({ n, label }) => (
    <div className="flex items-center gap-2" data-testid={`step-indicator-${n}`}>
      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${step > n ? "bg-primary text-primary-foreground" : step === n ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
        {step > n ? <Check className="w-3.5 h-3.5" /> : n}
      </div>
      <span className={`text-xs font-medium ${step >= n ? "text-foreground" : "text-muted-foreground"}`}>{label}</span>
    </div>
  );

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-16">
      <h1 className="font-display text-4xl font-semibold tracking-tight">Create your account</h1>
      <p className="text-muted-foreground mt-2">Free to start. Upgrade anytime.</p>

      <div className="flex items-center gap-4 mt-8">
        <StepDot n={1} label="Your details" />
        <div className="flex-1 h-px bg-border" />
        <StepDot n={2} label="Account type" />
      </div>

      <form onSubmit={submit} className="mt-8" noValidate>
        {step === 1 && <GoogleSignInButton label="Sign up with Google" />}
        {/* STEP 1 — demographics + opt-in (kept mounted so password persists on Back) */}
        <div className={step === 1 ? "space-y-5" : "hidden"}>
          <div>
            <Label htmlFor="name">Full name</Label>
            <Input id="name" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 rounded-sm" data-testid="register-name" />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 rounded-sm" data-testid="register-email" />
          </div>
          <div>
            <Label htmlFor="phone">Mobile phone number</Label>
            <Input id="phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+14155552671" className="mt-1.5 rounded-sm" data-testid="register-phone" />
            <p className="text-xs text-muted-foreground mt-1">Used for booking updates and account security. Enter in international format, e.g. +14155552671.</p>
          </div>

          <PasswordFields label="Password" onChange={(pw, v) => { set("password", pw); setPwValid(v); }} />

          {/* SMS opt-in — CTIA/TCPA compliant, checkbox NOT pre-selected */}
          <div className="rounded-sm border border-border bg-muted/40 p-4">
            <div className="flex items-start gap-3">
              <Checkbox id="sms_consent" checked={form.sms_consent} onCheckedChange={(v) => set("sms_consent", !!v)} className="mt-0.5" data-testid="register-sms-consent" />
              <Label htmlFor="sms_consent" className="text-sm font-medium leading-snug cursor-pointer">
                Yes, sign me up! Text me booking updates and appointment reminders.
              </Label>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed mt-3">
              By checking this box you agree to receive recurring automated texts from BookdHUB (booking
              confirmations and appointment reminders). Consent isn't a condition of purchase.
              Msg frequency varies. Msg &amp; data rates may apply. Reply <b>HELP</b> for help, <b>STOP</b> to cancel.
              See <Link to="/terms" className="text-primary underline" data-testid="register-terms-link">Terms of Service</Link> &amp;{" "}
              <Link to="/privacy" className="text-primary underline" data-testid="register-privacy-link">Privacy Policy</Link>.
            </p>
          </div>

          {error && <p className="text-destructive text-sm" data-testid="register-error">{error}</p>}
          <Button type="button" onClick={goNext} className="w-full rounded-sm" data-testid="register-continue">
            Continue
          </Button>
        </div>

        {/* STEP 2 — role / account type + CAPTCHA + submit */}
        <div className={step === 2 ? "space-y-5" : "hidden"}>
          <div>
            <Label className="text-sm">I want to join as</Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              {roles.map((r) => (
                <button key={r.key} type="button" onClick={() => set("role", r.key)}
                  data-testid={`role-${r.key}`}
                  className={`text-left border rounded-sm p-4 transition-all ${form.role === r.key ? "border-primary ring-2 ring-primary bg-primary/5" : "border-border hover:border-secondary"}`}>
                  <r.icon className="w-6 h-6 mb-2" strokeWidth={1.5} />
                  <div className="font-medium text-sm">{r.label}</div>
                  <div className="text-xs text-muted-foreground">{r.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {form.role === "customer" && (
            <div className="rounded-sm border border-border p-4 space-y-4" data-testid="customer-type-block">
              <div>
                <Label className="text-sm">Account type</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  {[["individual", "Individual", "Personal bookings"], ["business", "Business", "Book for an organization"]].map(([k, lbl, desc]) => (
                    <button key={k} type="button" onClick={() => set("customer_type", k)} data-testid={`customer-type-${k}`}
                      className={`text-left border rounded-sm p-3 transition-all ${form.customer_type === k ? "border-primary ring-2 ring-primary bg-primary/5" : "border-border hover:border-secondary"}`}>
                      <div className="font-medium text-sm">{lbl}</div>
                      <div className="text-xs text-muted-foreground">{desc}</div>
                    </button>
                  ))}
                </div>
              </div>
              {form.customer_type === "business" && (
                <div className="space-y-3" data-testid="business-fields">
                  <div>
                    <Label htmlFor="org_name" className="text-sm">Organization name</Label>
                    <Input id="org_name" value={form.org_name} onChange={(e) => set("org_name", e.target.value)} className="mt-1 rounded-sm" data-testid="register-org-name" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="org_title" className="text-sm">Your role / title</Label>
                      <Input id="org_title" value={form.org_title} onChange={(e) => set("org_title", e.target.value)} placeholder="e.g. Facilities Manager" className="mt-1 rounded-sm" data-testid="register-org-title" />
                    </div>
                    <div>
                      <Label htmlFor="org_tax_id" className="text-sm">Tax ID / EIN</Label>
                      <Input id="org_tax_id" value={form.org_tax_id} onChange={(e) => set("org_tax_id", e.target.value)} placeholder="12-3456789" className="mt-1 rounded-sm" data-testid="register-org-taxid" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="org_email" className="text-sm">Business email</Label>
                    <Input id="org_email" type="email" value={form.org_email} onChange={(e) => set("org_email", e.target.value)} className="mt-1 rounded-sm" data-testid="register-org-email" />
                  </div>
                  <div className="flex items-start gap-3 rounded-sm bg-muted/40 p-3">
                    <Checkbox id="authorized" checked={form.authorized} onCheckedChange={(v) => set("authorized", !!v)} className="mt-0.5" data-testid="register-authorized" />
                    <Label htmlFor="authorized" className="text-sm leading-snug cursor-pointer">
                      I confirm I am authorized to book and pay for services on behalf of {form.org_name || "my organization"}.
                    </Label>
                  </div>
                </div>
              )}
            </div>
          )}

          {error && <p className="text-destructive text-sm" data-testid="register-error-2">{error}</p>}
          {step === 2 && <Recaptcha ref={recaptchaRef} action="register" />}

          <div className="flex items-center gap-3">
            <Button type="button" variant="outline" onClick={() => { setError(""); setStep(1); }} className="rounded-sm" data-testid="register-back">
              <ArrowLeft className="w-4 h-4 mr-1.5" />Back
            </Button>
            <Button type="submit" disabled={loading} className="flex-1 rounded-sm" data-testid="register-submit">
              {loading ? "Creating…" : "Create account"}
            </Button>
          </div>
        </div>
      </form>

      <p className="mt-6 text-sm text-muted-foreground">
        Already have an account? <Link to={next ? `/login?next=${encodeURIComponent(next)}` : "/login"} className="text-primary font-medium">Log in</Link>
      </p>
    </div>
  );
}
